/**
 * Saving a bearer-authenticated file.
 *
 * Customer export and document routes (bookkeeping reports, the EIN correction
 * letter) re-verify the Better Auth JWT like every other
 * customer route, so `window.open(path)` or an `<a href>` reaches them with no
 * Authorization header and gets a 401. The bytes are fetched through
 * `productDataRaw` (which attaches the bearer and returns the raw Response),
 * turned into a blob, and handed to the browser through a synthetic anchor.
 *
 * `productData` is deliberately NOT used: it parses the body as JSON, which is
 * exactly wrong for a PDF.
 */
import { ProductDataError, productDataRaw } from '@/lib/api/product-data-client'

const QUOTED = /filename\s*=\s*"([^"]+)"/i
const BARE = /filename\s*=\s*([^;]+)/i

/**
 * The server's filename, or the caller's fallback.
 *
 * A name carrying `/` or `\` or starting with a dot is refused: it reaches the
 * user's filesystem, and sanitising it here is cheaper than trusting every
 * future writer of the export route.
 */
export function fileNameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback
  const matched = QUOTED.exec(header) ?? BARE.exec(header)
  const candidate = matched?.[1]?.trim()
  if (!candidate) return fallback
  if (candidate.includes('/') || candidate.includes('\\') || candidate.startsWith('.')) return fallback
  return candidate
}

export async function downloadWithAuth(path: string, fallbackFileName: string): Promise<void> {
  const response = await productDataRaw(path)
  if (!response.ok) {
    // The body is the API's `{ error: { code, message } }` envelope when the route refused the
    // request; its message already tells the customer what to change. Anything else (an empty
    // or non-JSON body) gets the generic one.
    const failure = await response
      .json()
      .then((body: { error?: { code?: unknown; message?: unknown } } | null) => body?.error)
      .catch(() => undefined)
    if (failure && typeof failure.message === 'string' && failure.message) {
      throw new ProductDataError(
        response.status,
        typeof failure.code === 'string' ? failure.code : 'HTTP_ERROR',
        failure.message,
      )
    }
    throw new Error('This file could not be downloaded. Please try again.')
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = fileNameFromDisposition(response.headers.get('Content-Disposition'), fallbackFileName)
    anchor.rel = 'noopener'
    anchor.click()
  } finally {
    // Revoked in the same turn: the anchor's click has already handed the blob
    // to the browser's download manager, and a URL left alive pins the bytes
    // in memory for the life of the document.
    URL.revokeObjectURL(objectUrl)
  }
}

/** Hands text the page already holds to the browser as a file. No request is made. */
export function downloadTextFile(fileName: string, text: string, mime = 'text/csv;charset=utf-8'): void {
  const objectUrl = URL.createObjectURL(new Blob([text], { type: mime }))
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = fileName
    anchor.rel = 'noopener'
    anchor.click()
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
